// File: src/components/Layout/Layout.tsx
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { useActiveLocation, useLoadLocationContext } from '../../hooks/useActiveLocation';
import { useFailedActions } from '../../features/notifications/useFailedActions';
import { usePendingOrders } from '../../hooks/usePendingOrders';
import { unlockSound, useSound } from '../../lib/sound';
import { InlineBanner } from '../ui/InlineBanner';
import { Toaster } from '../ui/toast';
import { BottomNav } from './BottomNav';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

/** App shell: sidebar or rail, top bar, scrolling <main>, bottom nav on phones and tablets. */
const Layout = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const main = useRef<HTMLElement>(null);
    const { blocked } = useSound();

    useLoadLocationContext();
    const { locationId } = useActiveLocation();
    usePendingOrders(locationId);
    useFailedActions();

    useEffect(() => {
        main.current?.scrollTo({ top: 0 });
    }, [pathname]);

    return (
        <div className="flex h-dvh bg-page">
            <a href="#main" className="skip-link">
                {t('shell.skip')}
            </a>
            <Sidebar />
            <div className="flex min-w-0 flex-1 flex-col">
                <TopBar />
                <main
                    id="main"
                    ref={main}
                    tabIndex={-1}
                    className="relative flex-1 overflow-y-auto pb-[calc(3.5rem+env(safe-area-inset-bottom))] text-body outline-none lg:pb-0"
                >
                    <div className="mx-auto w-full max-w-page px-gutter py-6">
                        {blocked && (
                            <InlineBanner tone="warning" className="mb-4" action={{ label: t('sound.enable'), onClick: () => void unlockSound() }}>
                                {t('sound.blocked')}
                            </InlineBanner>
                        )}
                        <Outlet />
                    </div>
                </main>
            </div>
            <BottomNav />
            <Toaster />
        </div>
    );
};

export default Layout;
